<template>
  <div>
    <b-container fluid class="mt-3">
      <div class="card mb-4">
        <div class="card-body d-flex flex-wrap align-items-end">
          <div class="mr-3 mb-2">
            <label class="small mb-1 d-block">{{ $t('analytics_date_range') }}</label>
            <el-date-picker
              v-model="dateRange"
              type="daterange"
              range-separator="—"
              :start-placeholder="$t('analytics_from')"
              :end-placeholder="$t('analytics_to')"
              format="dd-MM-yyyy"
              value-format="yyyy-MM-dd"
              :clearable="false"
              :picker-options="pickerOptions"
              @change="loadSummary"
            />
          </div>
          <div class="mr-3 mb-2">
            <label class="small mb-1 d-block">{{ $t('analytics_pais') }}</label>
            <el-select
              v-model="paisId"
              filterable
              clearable
              size="small"
              :placeholder="$t('analytics_pais_todos')"
              style="width: 220px;"
              @change="onPaisChange">
              <el-option
                v-for="p in paisesOptions"
                :key="p.pais_id"
                :label="p.label"
                :value="p.pais_id" />
            </el-select>
          </div>
          <div class="mr-3 mb-2">
            <label class="small mb-1 d-block">{{ $t('analytics_empresa') }}</label>
            <el-select
              v-model="empresaId"
              filterable
              clearable
              size="small"
              :placeholder="$t('analytics_empresa_todas')"
              style="width: 280px;"
              @change="loadSummary">
              <el-option
                v-for="e in empresasFiltradas"
                :key="e.empresa_id"
                :label="e.label"
                :value="e.empresa_id" />
            </el-select>
          </div>
          <div class="mr-3 mb-2">
            <button class="btn btn-outline-primary btn-sm" @click="loadSummary" :disabled="loading">
              <i class="fas fa-sync-alt" :class="{ 'fa-spin': loading }"></i>
              {{ $t('analytics_refresh') }}
            </button>
            <!-- Ayuda general de la pantalla: aca se van sumando las definiciones -->
            <button class="btn btn-outline-info btn-sm ml-2" @click="mostrarInfoGeneral">
              <i class="fas fa-info-circle"></i>
              {{ $t('analytics_info') }}
            </button>
          </div>
          <div v-if="summary && summary.period" class="ml-auto text-muted small">
            {{ $t('analytics_period') }}:
            {{ formatDate(summary.period.from) }} → {{ formatDate(summary.period.to) }}
          </div>
        </div>
      </div>

      <!-- Con filtro activo los totales no son comparables con la vista sin filtro: las
           busquedas y los cambios de filtro no tienen producto, asi que quedan fuera. -->
      <div v-if="empresaId || paisId" class="alert alert-info py-2 small" role="alert">
        <i class="fas fa-info-circle mr-1"></i> {{ $t('analytics_empresa_aviso') }}
      </div>

      <div v-if="error" class="alert alert-danger" role="alert">
        <i class="fas fa-exclamation-triangle"></i> {{ error }}
      </div>

      <div v-if="loading && !summary" class="text-center py-5">
        <i class="fas fa-spinner fa-spin fa-3x text-primary"></i>
        <p class="mt-3 text-muted">{{ $t('analytics_loading') }}</p>
      </div>

      <template v-if="summary">
        <!-- KPI cards -->
        <div class="d-flex align-items-center mb-2">
          <h3 class="mb-0">{{ $t('analytics_info_kpis_titulo') }}</h3>
          <button type="button" class="btn btn-link btn-sm p-0 ml-2 align-baseline"
            :title="$t('analytics_info_ver')" @click="mostrarInfo('kpis')">
            <i class="fas fa-info-circle"></i>
          </button>
        </div>
        <b-row>
          <b-col md="6" lg="3" v-for="kpi in kpis" :key="kpi.key" class="mb-3">
            <!-- La tarjeta abre el detalle de lo que compone el numero -->
            <div class="card card-stats h-100 kpi-clickable" role="button" tabindex="0"
              :title="$t('analytics_det_ver')"
              @click="abrirDetalle(kpi)" @keyup.enter="abrirDetalle(kpi)">
              <div class="card-body">
                <div class="row">
                  <div class="col">
                    <h5 class="card-title text-uppercase text-muted mb-0 small">
                      {{ kpi.label }}
                      <button type="button" class="btn btn-link btn-sm p-0 ml-1 align-baseline"
                        :title="$t('analytics_info_ver')" @click.stop="mostrarInfo(kpi.key)">
                        <i class="fas fa-info-circle"></i>
                      </button>
                    </h5>
                    <span class="h3 font-weight-bold mb-0">{{ formatNumber(kpi.value) }}</span>
                  </div>
                  <div class="col-auto">
                    <div class="icon icon-shape rounded-circle" :class="kpi.iconBg">
                      <i :class="kpi.icon"></i>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </b-col>
        </b-row>

        <!-- Distribuciones: los 3 graficos de torta van en una misma fila -->
        <b-row>
          <b-col md="4" class="mb-4">
            <div class="card h-100">
              <div class="card-header">
                <h3 class="mb-0">{{ $t('analytics_by_device') }}
                  <button type="button" class="btn btn-link btn-sm p-0 ml-2 align-baseline"
                    :title="$t('analytics_info_ver')" @click="mostrarInfo('by_device')">
                  <i class="fas fa-info-circle"></i>
                </button>
                </h3>
              </div>
              <div class="card-body">
                <div v-if="hasData(byDeviceChart)" class="pie-wrapper">
                  <pie-chart :chart-data="byDeviceChart" :options="pieOptions" />
                </div>
                <div v-else class="text-center text-muted py-5">{{ $t('analytics_no_data') }}</div>
              </div>
            </div>
          </b-col>
          <b-col md="4" class="mb-4">
            <div class="card h-100">
              <div class="card-header">
                <h3 class="mb-0">{{ $t('analytics_by_locale') }}
                  <button type="button" class="btn btn-link btn-sm p-0 ml-2 align-baseline"
                    :title="$t('analytics_info_ver')" @click="mostrarInfo('by_locale')">
                  <i class="fas fa-info-circle"></i>
                </button>
                </h3>
              </div>
              <div class="card-body">
                <div v-if="hasData(byLocaleChart)" class="pie-wrapper">
                  <pie-chart :chart-data="byLocaleChart" :options="pieOptions" />
                </div>
                <div v-else class="text-center text-muted py-5">{{ $t('analytics_no_data') }}</div>
              </div>
            </div>
          </b-col>
          <b-col md="4" class="mb-4">
            <div class="card h-100">
              <div class="card-header">
                <h3 class="mb-0">{{ $t('analytics_by_country') }}
                  <button type="button" class="btn btn-link btn-sm p-0 ml-2 align-baseline"
                    :title="$t('analytics_info_ver')" @click="mostrarInfo('by_country')">
                  <i class="fas fa-info-circle"></i>
                </button>
                </h3>
              </div>
              <div class="card-body">
                <div v-if="hasData(byCountryChart)" class="pie-wrapper">
                  <pie-chart :chart-data="byCountryChart" :options="pieOptions" />
                </div>
                <div v-else class="text-center text-muted py-5">{{ $t('analytics_no_data') }}</div>
              </div>
            </div>
          </b-col>
        </b-row>

        <!-- Top búsquedas y productos -->
        <b-row>
          <b-col md="6" class="mb-4">
            <div class="card h-100">
              <div class="card-header">
                <h3 class="mb-0">{{ $t('analytics_top_searches') }}
                  <button type="button" class="btn btn-link btn-sm p-0 ml-2 align-baseline"
                    :title="$t('analytics_info_ver')" @click="mostrarInfo('top_searches')">
                  <i class="fas fa-info-circle"></i>
                </button>
                </h3>
              </div>
              <div class="card-body">
                <!-- table-responsive es la red de seguridad: si algun termino igual no
                     entra, se hace scroll solo dentro de la tarjeta y no en toda la pagina -->
                <div v-if="summary.top_searches && summary.top_searches.length" class="table-responsive">
                  <table class="table table-sm mb-0" style="table-layout:fixed; width:100%;">
                    <thead>
                      <tr>
                        <th style="width:70%;">{{ $t('analytics_search_term') }}</th>
                        <th class="text-right" style="width:30%;">{{ $t('analytics_count') }}</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(row, i) in summary.top_searches" :key="'ts-'+i">
                        <td style="word-break:break-word;">{{ row.search_term }}</td>
                        <td class="text-right">{{ formatNumber(row.count) }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div v-else class="text-center text-muted py-5">{{ $t('analytics_no_data') }}</div>
              </div>
            </div>
          </b-col>
          <b-col md="6" class="mb-4">
            <div class="card h-100">
              <div class="card-header">
                <h3 class="mb-0">{{ $t('analytics_top_products_viewed') }}
                  <button type="button" class="btn btn-link btn-sm p-0 ml-2 align-baseline"
                    :title="$t('analytics_info_ver')" @click="mostrarInfo('top_products_viewed')">
                  <i class="fas fa-info-circle"></i>
                </button>
                </h3>
              </div>
              <div class="card-body">
                <div v-if="summary.top_products_viewed && summary.top_products_viewed.length" class="table-responsive">
                  <table class="table table-sm mb-0" style="table-layout:fixed; width:100%;">
                    <thead>
                      <tr>
                        <th style="width:70%;">{{ $t('analytics_product') }}</th>
                        <th class="text-right" style="width:30%;">{{ $t('analytics_views') }}</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(row, i) in summary.top_products_viewed" :key="'tp-'+i">
                        <td style="word-break:break-word;">{{ row.product_name || row.product_id }}</td>
                        <td class="text-right">{{ formatNumber(row.views) }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div v-else class="text-center text-muted py-5">{{ $t('analytics_no_data') }}</div>
              </div>
            </div>
          </b-col>
        </b-row>

        <!-- Top productos por impresiones -->
        <b-row>
          <b-col md="6" class="mb-4">
            <div class="card h-100">
              <div class="card-header">
                <h3 class="mb-0">{{ $t('analytics_top_products_impressed') }}
                  <button type="button" class="btn btn-link btn-sm p-0 ml-2 align-baseline"
                    :title="$t('analytics_info_ver')" @click="mostrarInfo('top_products_impressed')">
                  <i class="fas fa-info-circle"></i>
                </button>
                </h3>
                <small class="text-muted">{{ $t('analytics_impressions_hint') }}</small>
              </div>
              <div class="card-body">
                <div v-if="summary.top_products_impressed && summary.top_products_impressed.length" class="table-responsive">
                  <table class="table table-sm mb-0" style="table-layout:fixed; width:100%;">
                    <thead>
                      <tr>
                        <th style="width:70%;">{{ $t('analytics_product') }}</th>
                        <th class="text-right" style="width:30%;">{{ $t('analytics_impressions') }}</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(row, i) in summary.top_products_impressed" :key="'ti-'+i">
                        <td style="word-break:break-word;">{{ row.product_name || row.product_id }}</td>
                        <td class="text-right">{{ formatNumber(row.impressions) }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div v-else class="text-center text-muted py-5">{{ $t('analytics_no_data') }}</div>
              </div>
            </div>
          </b-col>
        </b-row>

        <!-- Top CTAs y zero results -->
        <b-row>
          <b-col md="6" class="mb-4">
            <div class="card h-100">
              <div class="card-header">
                <h3 class="mb-0">{{ $t('analytics_top_ctas') }}
                  <button type="button" class="btn btn-link btn-sm p-0 ml-2 align-baseline"
                    :title="$t('analytics_info_ver')" @click="mostrarInfo('top_ctas')">
                  <i class="fas fa-info-circle"></i>
                </button>
                </h3>
              </div>
              <div class="card-body">
                <div v-if="summary.top_ctas && summary.top_ctas.length" class="table-responsive">
                  <table class="table table-sm mb-0" style="table-layout:fixed; width:100%;">
                    <thead>
                      <tr>
                        <th style="width:70%;">{{ $t('analytics_cta_type') }}</th>
                        <th class="text-right" style="width:30%;">{{ $t('analytics_clicks') }}</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(row, i) in summary.top_ctas" :key="'cta-'+i">
                        <td style="word-break:break-word;">{{ row.cta_type }}</td>
                        <td class="text-right">{{ formatNumber(row.count) }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div v-else class="text-center text-muted py-5">{{ $t('analytics_no_data') }}</div>
              </div>
            </div>
          </b-col>
          <b-col md="6" class="mb-4">
            <div class="card h-100 border-warning">
              <div class="card-header bg-warning-soft">
                <h3 class="mb-0">
                  <i class="fas fa-search-minus text-warning"></i>
                  {{ $t('analytics_zero_result_searches') }}
                  <button type="button" class="btn btn-link btn-sm p-0 ml-2 align-baseline"
                    :title="$t('analytics_info_ver')" @click="mostrarInfo('zero_result_searches')">
                  <i class="fas fa-info-circle"></i>
                </button>
                </h3>
                <small class="text-muted">{{ $t('analytics_zero_result_hint') }}</small>
              </div>
              <div class="card-body">
                <div v-if="summary.zero_result_searches && summary.zero_result_searches.length" class="table-responsive">
                  <table class="table table-sm mb-0" style="table-layout:fixed; width:100%;">
                    <thead>
                      <tr>
                        <th style="width:70%;">{{ $t('analytics_search_term') }}</th>
                        <th class="text-right" style="width:30%;">{{ $t('analytics_attempts') }}</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(row, i) in summary.zero_result_searches" :key="'zr-'+i">
                        <td style="word-break:break-word;">{{ row.search_term }}</td>
                        <td class="text-right">{{ formatNumber(row.count) }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div v-else class="text-center text-muted py-5">{{ $t('analytics_no_data') }}</div>
              </div>
            </div>
          </b-col>
        </b-row>
      </template>
      <!-- Detalle de una tarjeta de KPI -->
      <b-modal v-model="modalDetalle" size="xl" ok-only :ok-title="$t('cerrar')" no-enforce-focus
        :title="detalleTitulo">
        <div v-if="detalleLoading" class="text-center py-4">
          <i class="fas fa-spinner fa-spin fa-2x text-primary"></i>
        </div>

        <div v-else>
          <div v-if="detalleError" class="alert alert-danger py-2 small" role="alert">
            <i class="fas fa-exclamation-triangle mr-1"></i> {{ $t('analytics_error_generic') }}
          </div>
          <div class="d-flex align-items-center justify-content-between mb-2">
            <div class="small text-muted">
              <span>{{ $t('analytics_det_total').replace('{n}', formatNumber(detalle.total)) }}</span>
              <span v-if="detalle.mode === 'impressions'" class="ml-2">
                · {{ $t('analytics_det_nota_impresiones') }}
              </span>
            </div>
            <button type="button" class="btn btn-outline-success btn-sm"
              :disabled="exportandoDetalle || !detalle.total" @click="exportarDetalleExcel">
              <i class="fas fa-spinner fa-spin mr-1" v-if="exportandoDetalle"></i>
              <i class="fas fa-file-excel mr-1" v-else></i>
              {{ exportandoDetalle ? $t('cargando') : $t('exportar_excel') }}
            </button>
          </div>

          <div v-if="!detalle.rows || !detalle.rows.length" class="text-center text-muted py-4">
            {{ $t('analytics_no_data') }}
          </div>

          <div v-else class="table-responsive detalle-scroll" style="max-height:60vh;overflow-y:auto;">
            <!-- Eventos: events, product_views, searches, cta_clicks -->
            <table v-if="detalle.mode === 'events'" class="table table-sm table-bordered table-hover mb-0">
              <thead class="thead-light">
                <tr>
                  <th class="th-ordenable" style="min-width:150px;" @click="ordenarDetalle('fecha')">
                    <input class="th-search-input" v-model="detalleFiltroCol.fecha" @click.stop placeholder="" />
                    <span class="th-label">{{ $t('analytics_det_fecha') }} <i :class="iconoOrdenDetalle('fecha')"></i></span>
                  </th>
                  <th class="th-ordenable" style="min-width:120px;" @click="ordenarDetalle('tipo')">
                    <input class="th-search-input" v-model="detalleFiltroCol.tipo" @click.stop placeholder="" />
                    <span class="th-label">{{ $t('analytics_det_tipo') }} <i :class="iconoOrdenDetalle('tipo')"></i></span>
                  </th>
                  <th class="th-ordenable" style="min-width:160px;" @click="ordenarDetalle('dato')">
                    <input class="th-search-input" v-model="detalleFiltroCol.dato" @click.stop placeholder="" />
                    <span class="th-label">{{ $t('analytics_det_dato') }} <i :class="iconoOrdenDetalle('dato')"></i></span>
                  </th>
                  <!-- Solo un icono, igual que la columna Link de /inventario: no se busca ni se ordena -->
                  <th class="text-center" style="width:70px;">{{ $t('analytics_det_pagina') }}</th>
                  <th class="th-ordenable" style="min-width:110px;" @click="ordenarDetalle('dispositivo')">
                    <input class="th-search-input" v-model="detalleFiltroCol.dispositivo" @click.stop placeholder="" />
                    <span class="th-label">{{ $t('analytics_det_dispositivo') }} <i :class="iconoOrdenDetalle('dispositivo')"></i></span>
                  </th>
                  <th class="th-ordenable" style="min-width:90px;" @click="ordenarDetalle('idioma')">
                    <input class="th-search-input" v-model="detalleFiltroCol.idioma" @click.stop placeholder="" />
                    <span class="th-label">{{ $t('analytics_det_idioma') }} <i :class="iconoOrdenDetalle('idioma')"></i></span>
                  </th>
                  <th class="th-ordenable" style="min-width:150px;" @click="ordenarDetalle('usuario')">
                    <input class="th-search-input" v-model="detalleFiltroCol.usuario" @click.stop placeholder="" />
                    <el-select v-model="detalleFiltroCol.usuarioSeleccion" multiple collapse-tags filterable size="mini"
                      :placeholder="$t('analytics_det_usuario')" class="th-filter-select" @click.native.stop>
                      <el-option v-for="u in usuariosVistosOptions" :key="u" :label="u" :value="u" />
                    </el-select>
                    <span class="th-label">{{ $t('analytics_det_usuario') }} <i :class="iconoOrdenDetalle('usuario')"></i></span>
                  </th>
                  <th class="th-ordenable" style="min-width:160px;" @click="ordenarDetalle('perfil')">
                    <el-select v-model="detalleFiltroCol.perfil" multiple collapse-tags filterable size="mini"
                      :placeholder="$t('analytics_det_perfil')" class="th-filter-select" @click.native.stop>
                      <el-option v-for="p in perfilesOptions" :key="p" :label="p" :value="p" />
                    </el-select>
                    <span class="th-label">{{ $t('analytics_det_perfil') }} <i :class="iconoOrdenDetalle('perfil')"></i></span>
                  </th>
                  <th class="th-ordenable" style="min-width:110px;" @click="ordenarDetalle('pais')">
                    <input class="th-search-input" v-model="detalleFiltroCol.pais" @click.stop placeholder="" />
                    <span class="th-label">{{ $t('analytics_det_pais') }} <i :class="iconoOrdenDetalle('pais')"></i></span>
                  </th>
                  <th class="th-ordenable" style="min-width:140px;" @click="ordenarDetalle('empresa')">
                    <input class="th-search-input" v-model="detalleFiltroCol.empresa" @click.stop placeholder="" />
                    <span class="th-label">{{ $t('analytics_det_empresa') }} <i :class="iconoOrdenDetalle('empresa')"></i></span>
                  </th>
                  <th class="th-ordenable" style="min-width:120px;" @click="ordenarDetalle('sesion')">
                    <input class="th-search-input" v-model="detalleFiltroCol.sesion" @click.stop placeholder="" />
                    <span class="th-label">{{ $t('analytics_det_sesion') }} <i :class="iconoOrdenDetalle('sesion')"></i></span>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(r, i) in detalleFilas" :key="i">
                  <td class="text-nowrap">{{ formatDateTime(r.created_at) }}</td>
                  <td>
                    <span class="badge" :class="claseTipoEvento(r.event_type)" :title="r.event_type">
                      {{ etiquetaTipo(r) }}
                    </span>
                  </td>
                  <td class="td-elipsis" :title="datoPrincipal(r)">{{ datoPrincipal(r) || '—' }}</td>
                  <td class="text-center">
                    <!-- Mismo icono que la columna Link de Inventario, para no inventar
                         otra convencion visual para lo mismo -->
                    <a v-if="r.page_path" :href="urlPagina(r.page_path)" target="_blank"
                      rel="noopener noreferrer" :title="urlPagina(r.page_path)"
                      style="font-size:1.2rem; text-decoration:none;">🌐</a>
                    <span v-else class="text-muted">—</span>
                  </td>
                  <td>{{ r.device_type || '—' }}</td>
                  <td>{{ r.locale || '—' }}</td>
                  <td class="td-elipsis" :title="r.user_id">{{ nombreUsuario(r.user_id) }}</td>
                  <td class="td-elipsis" :title="perfilUsuario(r.user_id)">{{ perfilUsuario(r.user_id) || '—' }}</td>
                  <td class="td-elipsis" :title="paisUsuario(r.user_id)">{{ paisUsuario(r.user_id) || '—' }}</td>
                  <td class="td-elipsis" :title="empresaUsuario(r.user_id)">{{ empresaUsuario(r.user_id) || '—' }}</td>
                  <td class="td-elipsis" :title="r.session_id">{{ acorta(r.session_id) }}</td>
                </tr>
                <tr v-if="!detalleFilas.length">
                  <td colspan="11" class="text-center text-muted py-3">{{ $t('analytics_no_data') }}</td>
                </tr>
              </tbody>
            </table>

            <!-- Agrupado: unique_sessions y unique_users -->
            <table v-else-if="detalle.mode === 'sessions' || detalle.mode === 'users'"
              class="table table-sm table-bordered table-hover mb-0">
              <thead class="thead-light">
                <tr>
                  <th class="th-ordenable" style="min-width:150px;" @click="ordenarDetalle('clave')">
                    <input class="th-search-input" v-model="detalleFiltroCol.clave" @click.stop placeholder="" />
                    <!-- En 'users' la clave YA es el usuario: el checklist va sobre esta misma columna. -->
                    <el-select v-if="detalle.mode === 'users'" v-model="detalleFiltroCol.claveSeleccion" multiple collapse-tags filterable size="mini"
                      :placeholder="$t('analytics_det_usuario')" class="th-filter-select" @click.native.stop>
                      <el-option v-for="u in usuariosVistosOptions" :key="u" :label="u" :value="u" />
                    </el-select>
                    <span class="th-label">
                      {{ detalle.mode === 'users' ? $t('analytics_det_usuario') : $t('analytics_det_sesion') }}
                      <i :class="iconoOrdenDetalle('clave')"></i>
                    </span>
                  </th>
                  <th v-if="detalle.mode === 'sessions'" class="th-ordenable" style="min-width:150px;" @click="ordenarDetalle('usuario')">
                    <input class="th-search-input" v-model="detalleFiltroCol.usuario" @click.stop placeholder="" />
                    <el-select v-model="detalleFiltroCol.usuarioSeleccion" multiple collapse-tags filterable size="mini"
                      :placeholder="$t('analytics_det_usuario')" class="th-filter-select" @click.native.stop>
                      <el-option v-for="u in usuariosVistosOptions" :key="u" :label="u" :value="u" />
                    </el-select>
                    <span class="th-label">{{ $t('analytics_det_usuario') }} <i :class="iconoOrdenDetalle('usuario')"></i></span>
                  </th>
                  <!-- Juntas: Usuario/Sesion, Perfil, Pais y Empresa. -->
                  <th class="th-ordenable" style="min-width:160px;" @click="ordenarDetalle('perfil')">
                    <el-select v-model="detalleFiltroCol.perfil" multiple collapse-tags filterable size="mini"
                      :placeholder="$t('analytics_det_perfil')" class="th-filter-select" @click.native.stop>
                      <el-option v-for="p in perfilesOptions" :key="p" :label="p" :value="p" />
                    </el-select>
                    <span class="th-label">{{ $t('analytics_det_perfil') }} <i :class="iconoOrdenDetalle('perfil')"></i></span>
                  </th>
                  <th class="th-ordenable" style="min-width:110px;" @click="ordenarDetalle('pais')">
                    <input class="th-search-input" v-model="detalleFiltroCol.pais" @click.stop placeholder="" />
                    <span class="th-label">{{ $t('analytics_det_pais') }} <i :class="iconoOrdenDetalle('pais')"></i></span>
                  </th>
                  <th class="th-ordenable" style="min-width:140px;" @click="ordenarDetalle('empresa')">
                    <input class="th-search-input" v-model="detalleFiltroCol.empresa" @click.stop placeholder="" />
                    <span class="th-label">{{ $t('analytics_det_empresa') }} <i :class="iconoOrdenDetalle('empresa')"></i></span>
                  </th>
                  <th class="th-ordenable text-center" style="width:110px;" @click="ordenarDetalle('eventos')">
                    <input class="th-search-input" v-model="detalleFiltroCol.eventos" @click.stop placeholder="" />
                    <span class="th-label">{{ $t('analytics_det_eventos') }} <i :class="iconoOrdenDetalle('eventos')"></i></span>
                  </th>
                  <th class="th-ordenable" style="min-width:150px;" @click="ordenarDetalle('primero')">
                    <input class="th-search-input" v-model="detalleFiltroCol.primero" @click.stop placeholder="" />
                    <span class="th-label">{{ $t('analytics_det_primero') }} <i :class="iconoOrdenDetalle('primero')"></i></span>
                  </th>
                  <th class="th-ordenable" style="min-width:150px;" @click="ordenarDetalle('ultimo')">
                    <input class="th-search-input" v-model="detalleFiltroCol.ultimo" @click.stop placeholder="" />
                    <span class="th-label">{{ $t('analytics_det_ultimo') }} <i :class="iconoOrdenDetalle('ultimo')"></i></span>
                  </th>
                  <th class="th-ordenable" style="min-width:110px;" @click="ordenarDetalle('dispositivo')">
                    <input class="th-search-input" v-model="detalleFiltroCol.dispositivo" @click.stop placeholder="" />
                    <span class="th-label">{{ $t('analytics_det_dispositivo') }} <i :class="iconoOrdenDetalle('dispositivo')"></i></span>
                  </th>
                  <th class="th-ordenable" style="min-width:90px;" @click="ordenarDetalle('idioma')">
                    <input class="th-search-input" v-model="detalleFiltroCol.idioma" @click.stop placeholder="" />
                    <span class="th-label">{{ $t('analytics_det_idioma') }} <i :class="iconoOrdenDetalle('idioma')"></i></span>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(r, i) in detalleFilas" :key="i">
                  <td class="td-elipsis" :title="r.key">{{ detalle.mode === 'users' ? nombreUsuario(r.key) : acorta(r.key) }}</td>
                  <td v-if="detalle.mode === 'sessions'" class="td-elipsis" :title="r.user_id">{{ nombreUsuario(r.user_id) }}</td>
                  <td class="td-elipsis" :title="perfilUsuario(idUsuarioDetalle(r))">{{ perfilUsuario(idUsuarioDetalle(r)) || '—' }}</td>
                  <td class="td-elipsis" :title="paisUsuario(idUsuarioDetalle(r))">{{ paisUsuario(idUsuarioDetalle(r)) || '—' }}</td>
                  <td class="td-elipsis" :title="empresaUsuario(idUsuarioDetalle(r))">{{ empresaUsuario(idUsuarioDetalle(r)) || '—' }}</td>
                  <td class="text-center">{{ formatNumber(r.count) }}</td>
                  <td class="text-nowrap">{{ formatDateTime(r.first) }}</td>
                  <td class="text-nowrap">{{ formatDateTime(r.last) }}</td>
                  <td>{{ r.device_type || '—' }}</td>
                  <td>{{ r.locale || '—' }}</td>
                </tr>
                <tr v-if="!detalleFilas.length">
                  <td :colspan="detalle.mode === 'sessions' ? 10 : 9" class="text-center text-muted py-3">{{ $t('analytics_no_data') }}</td>
                </tr>
              </tbody>
            </table>

            <!-- Impresiones: una fila por producto mostrado -->
            <table v-else class="table table-sm table-bordered table-hover mb-0">
              <thead class="thead-light">
                <tr>
                  <th class="th-ordenable" style="min-width:180px;" @click="ordenarDetalle('producto')">
                    <input class="th-search-input" v-model="detalleFiltroCol.producto" @click.stop placeholder="" />
                    <span class="th-label">{{ $t('analytics_det_producto') }} <i :class="iconoOrdenDetalle('producto')"></i></span>
                  </th>
                  <th class="th-ordenable text-center" style="width:140px;" @click="ordenarDetalle('veces')">
                    <input class="th-search-input" v-model="detalleFiltroCol.veces" @click.stop placeholder="" />
                    <span class="th-label">{{ $t('analytics_det_veces') }} <i :class="iconoOrdenDetalle('veces')"></i></span>
                  </th>
                  <th class="th-ordenable" style="min-width:150px;" @click="ordenarDetalle('ultimo')">
                    <input class="th-search-input" v-model="detalleFiltroCol.ultimo" @click.stop placeholder="" />
                    <span class="th-label">{{ $t('analytics_det_ultimo') }} <i :class="iconoOrdenDetalle('ultimo')"></i></span>
                  </th>
                  <th class="th-ordenable" style="min-width:110px;" @click="ordenarDetalle('pais')">
                    <input class="th-search-input" v-model="detalleFiltroCol.pais" @click.stop placeholder="" />
                    <span class="th-label">{{ $t('analytics_det_pais') }} <i :class="iconoOrdenDetalle('pais')"></i></span>
                  </th>
                  <th class="th-ordenable" style="min-width:140px;" @click="ordenarDetalle('empresa')">
                    <input class="th-search-input" v-model="detalleFiltroCol.empresa" @click.stop placeholder="" />
                    <span class="th-label">{{ $t('analytics_det_empresa') }} <i :class="iconoOrdenDetalle('empresa')"></i></span>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(r, i) in detalleFilas" :key="i">
                  <td class="td-elipsis" :title="r.label || r.key">{{ r.label || r.key }}</td>
                  <td class="text-center">{{ formatNumber(r.count) }}</td>
                  <td class="text-nowrap">{{ formatDateTime(r.last) }}</td>
                  <td class="td-elipsis" :title="paisProducto(r)">{{ paisProducto(r) || '—' }}</td>
                  <td class="td-elipsis" :title="empresaProducto(r)">{{ empresaProducto(r) || '—' }}</td>
                </tr>
                <tr v-if="!detalleFilas.length">
                  <td colspan="5" class="text-center text-muted py-3">{{ $t('analytics_no_data') }}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div v-if="detallePaginas > 1" class="d-flex align-items-center justify-content-end mt-3">
            <button class="btn btn-sm btn-outline-secondary mr-2"
              :disabled="detalle.page <= 1" @click="cambiarPaginaDetalle(detalle.page - 1)">
              <i class="fas fa-chevron-left"></i>
            </button>
            <span class="small text-muted">
              {{ $t('analytics_det_pagina_de').replace('{p}', detalle.page).replace('{t}', detallePaginas) }}
            </span>
            <button class="btn btn-sm btn-outline-secondary ml-2"
              :disabled="detalle.page >= detallePaginas" @click="cambiarPaginaDetalle(detalle.page + 1)">
              <i class="fas fa-chevron-right"></i>
            </button>
          </div>
        </div>
      </b-modal>
    </b-container>
  </div>
</template>

<script>
import { DatePicker, Select, Option } from 'element-ui';
import Swal from 'sweetalert2';
import analyticsService from '@/api/analyticsService';
import * as XLSX from 'xlsx';
import empresaService from '@/api/empresaService';
import paisService from '@/api/paisService';
import userService from '@/api/userService';
import utilsService from '@/api/utilsService';
import PieChart from '@/components/Charts/PieChart';

const PIE_COLORS = ['#5e72e4', '#11cdef', '#2dce89', '#fb6340', '#f5365c', '#ffd600', '#8965e0', '#f3a4b5'];

const PIE_OPTIONS = {
  responsive: true,
  maintainAspectRatio: true,
  aspectRatio: 1,
  legend: {
    display: true,
    position: 'top',
    labels: { usePointStyle: true, fontColor: '#000', fontSize: 13 },
  },
};

export default {
  name: 'AnalyticsDashboard',
  components: {
    PieChart,
    [DatePicker.name]: DatePicker,
    [Select.name]: Select,
    [Option.name]: Option,
  },
  data() {
    const today = new Date();
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(today.getDate() - 30);
    return {
      dateRange: [this.toIsoDate(thirtyDaysAgo), this.toIsoDate(today)],
      summary: null,
      loading: false,
      error: '',
      empresaId: null,
      // Pais de la empresa duena del material, no el de la bodega ni el del visitante:
      // es el que trae /analytics/empresas y el que hace que el selector de empresas y
      // las metricas hablen del mismo recorte.
      paisId: null,
      empresasOptions: [],
      paisesById: {},
      perfilesOptions: [],
      modalDetalle: false,
      exportandoDetalle: false,
      detalleKpi: null,
      detalleLoading: false,
      detalleLimit: 50,
      detalle: { rows: [], total: 0, page: 1, mode: 'events' },
      usuariosById: {},
      detalleError: false,
      // Busqueda y orden por columna del modal de detalle, igual que en /inventario.
      // Se aplican sobre la pagina ya cargada, no contra el backend: filtrar de nuevo
      // 50 filas en el navegador es instantaneo y no exige otro endpoint.
      detalleFiltroCol: {},
      detalleOrdenColumna: '',
      detalleOrdenDireccion: 'asc',
      pieOptions: PIE_OPTIONS,
      pickerOptions: {
        shortcuts: [
          {
            text: this.$t('analytics_last_7_days'),
            onClick(picker) {
              const end = new Date();
              const start = new Date();
              start.setDate(start.getDate() - 7);
              picker.$emit('pick', [start, end]);
            },
          },
          {
            text: this.$t('analytics_last_30_days'),
            onClick(picker) {
              const end = new Date();
              const start = new Date();
              start.setDate(start.getDate() - 30);
              picker.$emit('pick', [start, end]);
            },
          },
          {
            text: this.$t('analytics_last_90_days'),
            onClick(picker) {
              const end = new Date();
              const start = new Date();
              start.setDate(start.getDate() - 90);
              picker.$emit('pick', [start, end]);
            },
          },
        ],
      },
    };
  },
  computed: {
    kpis() {
      const t = (this.summary && this.summary.totals) || {};
      return [
        { key: 'events', label: this.$t('analytics_kpi_events'), value: t.events || 0,
          icon: 'fas fa-stream text-white', iconBg: 'bg-primary' },
        { key: 'unique_sessions', label: this.$t('analytics_kpi_unique_sessions'), value: t.unique_sessions || 0,
          icon: 'fas fa-globe text-white', iconBg: 'bg-info' },
        { key: 'unique_users', label: this.$t('analytics_kpi_unique_users'), value: t.unique_users || 0,
          icon: 'fas fa-user text-white', iconBg: 'bg-success' },
        { key: 'product_views', label: this.$t('analytics_kpi_product_views'), value: t.product_views || 0,
          icon: 'fas fa-eye text-white', iconBg: 'bg-warning' },
        { key: 'impressions', label: this.$t('analytics_kpi_impressions'), value: t.impressions || 0,
          icon: 'fas fa-images text-white', iconBg: 'bg-warning' },
        { key: 'searches', label: this.$t('analytics_kpi_searches'), value: t.searches || 0,
          icon: 'fas fa-search text-white', iconBg: 'bg-danger' },
        { key: 'cta_clicks', label: this.$t('analytics_kpi_cta_clicks'), value: t.cta_clicks || 0,
          icon: 'fas fa-mouse-pointer text-white', iconBg: 'bg-default' },
      ];
    },
    byDeviceChart() {
      return this.buildPieChart(this.summary && this.summary.by_device);
    },

    // Solo los paises que tienen empresas con catalogo: ofrecer dbo.Paises entero llenaria
    // el selector de opciones que no devuelven nada, igual que se hace con las empresas.
    paisesOptions() {
      const vistos = new Map();
      this.empresasOptions.forEach(e => {
        if (e.pais_id == null) return;
        const id = Number(e.pais_id);
        if (!vistos.has(id)) vistos.set(id, this.paisesById[String(id)] || `#${id}`);
      });
      return Array.from(vistos, ([pais_id, label]) => ({ pais_id, label }))
        .sort((a, b) => a.label.localeCompare(b.label));
    },

    // El selector de empresas se recorta al pais elegido. Una empresa sin pais asignado
    // queda fuera al filtrar: no hay forma de saber si pertenece al pais que se pidio.
    empresasFiltradas() {
      if (!this.paisId) return this.empresasOptions;
      return this.empresasOptions.filter(e => Number(e.pais_id) === Number(this.paisId));
    },
    byLocaleChart() {
      return this.buildPieChart(this.summary && this.summary.by_locale);
    },
    // La API devuelve ids de dbo.Paises; el nombre se resuelve aca, que es donde hay
    // acceso a SQL. Un id sin nombre se muestra tal cual antes que desaparecer del grafico.
    detalleTitulo() {
      return this.detalleKpi
        ? this.$t('analytics_det_titulo').replace('{kpi}', this.detalleKpi.label)
        : '';
    },
    detallePaginas() {
      const total = (this.detalle && this.detalle.total) || 0;
      return Math.max(1, Math.ceil(total / this.detalleLimit));
    },

    // Checklist de Usuario: solo tiene sentido ofrecer los que aparecen en la pagina
    // cargada (no hay catalogo global de usuarios del ecommerce), igual de local que el
    // resto de los filtros de este modal. El texto libre sigue sirviendo para buscar a
    // alguien que no este entre estas opciones.
    // Nombre de empresa por id, para resolver el dueño del producto en las filas de
    // Impresiones (empresasOptions ya esta cargado para el filtro de arriba).
    empresasPorId() {
      const map = {};
      (this.empresasOptions || []).forEach(e => { map[String(e.empresa_id)] = e.nombre; });
      return map;
    },

    usuariosVistosOptions() {
      const modo = this.detalle && this.detalle.mode;
      if (modo !== 'events' && modo !== 'sessions' && modo !== 'users') return [];
      const rows = (this.detalle && this.detalle.rows) || [];
      const vistos = new Set();
      rows.forEach(r => vistos.add(modo === 'users' ? this.nombreUsuario(r.key) : this.nombreUsuario(r.user_id)));
      return Array.from(vistos).sort((a, b) => a.localeCompare(b, 'es'));
    },

    // Que campo mostrar/buscar/ordenar por columna, segun el modo del detalle: los mismos
    // r.key y r.user_id significan cosas distintas en sesiones, usuarios o impresiones.
    // "valor" es lo que se compara al buscar y ordenar; cuando no hay "texto" propio se
    // muestra el mismo valor formateado como texto.
    detalleColumnas() {
      const modo = this.detalle.mode;

      if (modo === 'sessions' || modo === 'users') {
        const cols = [
          { campo: 'clave', valor: r => (modo === 'users' ? this.nombreUsuario(r.key) : r.key) },
        ];
        if (modo === 'users') {
          // En 'users' la clave YA es el usuario: el checklist filtra sobre el mismo valor.
          cols.push({ campo: 'claveSeleccion', valor: r => this.nombreUsuario(r.key) });
        }
        if (modo === 'sessions') {
          cols.push({ campo: 'usuario', valor: r => this.nombreUsuario(r.user_id) });
          cols.push({ campo: 'usuarioSeleccion', valor: r => this.nombreUsuario(r.user_id) });
        }
        // Juntas: Usuario/Sesion, Perfil, Pais y Empresa.
        cols.push({ campo: 'perfil', valor: r => this.perfilUsuario(this.idUsuarioDetalle(r)) });
        cols.push({ campo: 'pais', valor: r => this.paisUsuario(this.idUsuarioDetalle(r)) });
        cols.push({ campo: 'empresa', valor: r => this.empresaUsuario(this.idUsuarioDetalle(r)) });
        cols.push(
          { campo: 'eventos', valor: r => r.count, numerico: true },
          { campo: 'primero', valor: r => r.first, fecha: true },
          { campo: 'ultimo', valor: r => r.last, fecha: true },
          { campo: 'dispositivo', valor: r => r.device_type },
          { campo: 'idioma', valor: r => r.locale },
        );
        return cols;
      }

      if (modo === 'impressions') {
        return [
          { campo: 'producto', valor: r => r.label || r.key },
          { campo: 'veces', valor: r => r.count, numerico: true },
          { campo: 'ultimo', valor: r => r.last, fecha: true },
          { campo: 'pais', valor: r => this.paisProducto(r) },
          { campo: 'empresa', valor: r => this.empresaProducto(r) },
        ];
      }

      // events, product_views, searches, cta_clicks. La columna Pagina queda afuera: es
      // solo un icono, igual que la columna Link de /inventario, que tampoco se busca ni
      // se ordena.
      return [
        { campo: 'fecha', valor: r => r.created_at, fecha: true },
        { campo: 'tipo', valor: r => this.etiquetaTipo(r) },
        { campo: 'dato', valor: r => this.datoPrincipal(r) },
        { campo: 'dispositivo', valor: r => r.device_type },
        { campo: 'idioma', valor: r => r.locale },
        { campo: 'usuario', valor: r => this.nombreUsuario(r.user_id) },
        { campo: 'usuarioSeleccion', valor: r => this.nombreUsuario(r.user_id) },
        { campo: 'perfil', valor: r => this.perfilUsuario(r.user_id) },
        { campo: 'pais', valor: r => this.paisUsuario(r.user_id) },
        { campo: 'empresa', valor: r => this.empresaUsuario(r.user_id) },
        { campo: 'sesion', valor: r => r.session_id },
      ];
    },

    // Filas de la pagina actual, filtradas y ordenadas segun el head de la tabla. No pega
    // al backend: opera sobre lo que ya se cargo, igual que el resto de las tablas de
    // /inventario filtra sobre lo que tiene en memoria.
    detalleFilas() {
      const filas = (this.detalle && this.detalle.rows) || [];
      const columnas = this.detalleColumnas;

      let out = filas.filter(r => {
        for (const campo in this.detalleFiltroCol) {
          const filtro = this.detalleFiltroCol[campo];
          const col = columnas.find(c => c.campo === campo);
          if (!col) continue;

          // perfil es un checklist (multi-select): coincide si el valor de la fila esta
          // entre los elegidos. Un array vacio significa "sin filtro", igual que texto vacio.
          if (Array.isArray(filtro)) {
            if (!filtro.length) continue;
            if (!filtro.includes(col.valor(r))) return false;
            continue;
          }

          const needle = String(filtro || '').trim().toLowerCase();
          if (!needle) continue;
          const val = col.valor(r);
          const texto = col.fecha ? this.formatDateTime(val) : val;
          if (String(texto == null ? '' : texto).toLowerCase().indexOf(needle) === -1) return false;
        }
        return true;
      });

      if (this.detalleOrdenColumna) {
        const col = columnas.find(c => c.campo === this.detalleOrdenColumna);
        if (col) {
          const dir = this.detalleOrdenDireccion === 'asc' ? 1 : -1;
          out = out.slice().sort((a, b) => {
            const va = col.valor(a);
            const vb = col.valor(b);
            if (col.fecha) {
              return ((va ? new Date(va).getTime() : 0) - (vb ? new Date(vb).getTime() : 0)) * dir;
            }
            if (col.numerico) {
              return ((Number(va) || 0) - (Number(vb) || 0)) * dir;
            }
            return String(va == null ? '' : va).localeCompare(String(vb == null ? '' : vb)) * dir;
          });
        }
      }

      return out;
    },
    byCountryChart() {
      const raw = (this.summary && this.summary.by_country) || {};
      const conNombre = {};
      Object.keys(raw).forEach(id => {
        const nombre = this.paisesById[String(id)] || `#${id}`;
        conNombre[nombre] = (conNombre[nombre] || 0) + raw[id];
      });
      return this.buildPieChart(conNombre);
    },
  },
  methods: {
    /**
     * Que mide cada cuadro. Se define aca y no como HTML en los archivos de idioma para
     * que las traducciones sigan siendo texto plano y el detalle de los CTA se pueda
     * recorrer como datos.
     */
    definicionesInfo() {
      return {
        kpis: { titulo: this.$t('analytics_info_kpis_titulo'), texto: this.$t('analytics_info_kpis') },
        // Una entrada por tarjeta de Indicadores, ademas del resumen conjunto de arriba: el
        // usuario que no conoce el tema abre el icono de la tarjeta puntual, no el general.
        events: { titulo: this.$t('analytics_kpi_events'), texto: this.$t('analytics_info_kpi_events') },
        unique_sessions: { titulo: this.$t('analytics_kpi_unique_sessions'), texto: this.$t('analytics_info_kpi_unique_sessions') },
        unique_users: { titulo: this.$t('analytics_kpi_unique_users'), texto: this.$t('analytics_info_kpi_unique_users') },
        product_views: { titulo: this.$t('analytics_kpi_product_views'), texto: this.$t('analytics_info_kpi_product_views') },
        impressions: { titulo: this.$t('analytics_kpi_impressions'), texto: this.$t('analytics_info_kpi_impressions') },
        searches: { titulo: this.$t('analytics_kpi_searches'), texto: this.$t('analytics_info_kpi_searches') },
        cta_clicks: { titulo: this.$t('analytics_kpi_cta_clicks'), texto: this.$t('analytics_info_kpi_cta_clicks') },
        by_device: { titulo: this.$t('analytics_by_device'), texto: this.$t('analytics_info_by_device') },
        by_locale: { titulo: this.$t('analytics_by_locale'), texto: this.$t('analytics_info_by_locale') },
        by_country: { titulo: this.$t('analytics_by_country'), texto: this.$t('analytics_info_by_country') },
        top_searches: { titulo: this.$t('analytics_top_searches'), texto: this.$t('analytics_info_top_searches') },
        top_products_viewed: { titulo: this.$t('analytics_top_products_viewed'), texto: this.$t('analytics_info_top_products_viewed') },
        top_products_impressed: { titulo: this.$t('analytics_top_products_impressed'), texto: this.$t('analytics_info_top_products_impressed') },
        zero_result_searches: { titulo: this.$t('analytics_zero_result_searches'), texto: this.$t('analytics_info_zero_result_searches') },
        top_ctas: {
          titulo: this.$t('analytics_top_ctas'),
          texto: this.$t('analytics_info_top_ctas'),
          // Cada fila es un cta_type tal como se guarda en analytics_events.
          filas: [
            { clave: 'download_pdf', desc: this.$t('analytics_cta_download_pdf') },
            { clave: 'request_product', desc: this.$t('analytics_cta_request_product') },
            { clave: 'copy_link', desc: this.$t('analytics_cta_copy_link') },
            { clave: 'enter_offer', desc: this.$t('analytics_cta_enter_offer') },
            { clave: 'make_offer', desc: this.$t('analytics_cta_make_offer') },
            { clave: 'register_seller', desc: this.$t('analytics_cta_register_seller') },
          ],
          nota: this.$t('analytics_info_top_ctas_nota'),
        },
      };
    },

    escaparHtml(v) {
      return String(v == null ? '' : v)
        .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
    },

    /**
     * Ayuda general de la pantalla. Es el lugar donde se van sumando las definiciones de
     * lo que el usuario ve, para que no tenga que adivinar que significa cada dato.
     *
     * Los tipos de evento se listan como datos y no como HTML dentro de los archivos de
     * idioma: asi las traducciones siguen siendo texto plano y la tabla se puede recorrer.
     */
    tiposDeEvento() {
      return [
        { tipo: 'view_item_list', clave: 'analytics_ev_view_item_list' },
        { tipo: 'product_view',   clave: 'analytics_ev_product_view' },
        { tipo: 'search',         clave: 'analytics_ev_search' },
        { tipo: 'filter_apply',   clave: 'analytics_ev_filter_apply' },
        { tipo: 'select_item',    clave: 'analytics_ev_select_item' },
        { tipo: 'cta_click',      clave: 'analytics_ev_cta_click' },
        { tipo: 'modal_open',     clave: 'analytics_ev_modal_open' },
        { tipo: 'form_submit',    clave: 'analytics_ev_form_submit' },
        { tipo: 'engagement',     clave: 'analytics_ev_engagement' },
      ];
    },

    mostrarInfoGeneral() {
      const esc = v => this.escaparHtml(v);

      // Los estilos van inline y no en el <style scoped> del componente: SweetAlert monta
      // su contenido fuera del arbol del componente, asi que el scoped no lo alcanza.
      const TD = 'padding:6px 8px; vertical-align:top; border-top:1px solid #e9ecef;';
      const TXT = 'font-size:13px; line-height:1.45; color:#525f7f; word-break:break-word; white-space:normal;';

      const filas = this.tiposDeEvento()
        .map(e => `<tr>`
          + `<td style="${TD} width:132px;">`
          + `<span class="badge ${this.claseTipoEvento(e.tipo)}" `
          + `style="font-size:10px; white-space:normal; word-break:break-word;">${esc(e.tipo)}</span>`
          + `</td>`
          + `<td style="${TD} ${TXT}">${esc(this.$t(e.clave))}</td>`
          + `</tr>`)
        .join('');

      // table-layout fija los anchos: sin eso la columna de texto se estira y desborda.
      const tablaEventos =
          `<table style="width:100%; table-layout:fixed; border-collapse:collapse; margin-top:8px;">`
        + `<thead><tr>`
        + `<th style="${TD} width:132px; font-size:11px; text-transform:uppercase; color:#8898aa;">`
        + `${esc(this.$t('analytics_det_tipo'))}</th>`
        + `<th style="${TD} font-size:11px; text-transform:uppercase; color:#8898aa;">`
        + `${esc(this.$t('analytics_info_cuando_se_registra'))}</th>`
        + `</tr></thead><tbody>${filas}</tbody></table>`;

      const titulo = t =>
        `<div style="font-weight:600; color:#32325d; margin:0 0 4px;">${esc(t)}</div>`;

      const parrafo = t => `<div style="${TXT}">${esc(t)}</div>`;

      const lista = claves =>
        `<ul style="${TXT} margin:6px 0 0; padding-left:18px;">`
        + claves.map(k => `<li style="margin-bottom:4px;">${esc(this.$t(k))}</li>`).join('')
        + `</ul>`;

      const html =
          `<div style="text-align:left; max-height:62vh; overflow-y:auto; overflow-x:hidden; padding-right:4px;">`
        + `<div style="margin-bottom:16px;">`
        +   titulo(this.$t('analytics_info_gen_que_es_titulo'))
        +   parrafo(this.$t('analytics_info_gen_que_es'))
        + `</div>`
        + `<div style="margin-bottom:16px;">`
        +   titulo(this.$t('analytics_info_gen_eventos_titulo'))
        +   parrafo(this.$t('analytics_info_gen_eventos'))
        +   tablaEventos
        + `</div>`
        + `<div>`
        +   titulo(this.$t('analytics_info_gen_ojo_titulo'))
        +   lista(['analytics_info_gen_ojo_locale',
                   'analytics_info_gen_ojo_impresiones',
                   'analytics_info_gen_ojo_empresa',
                   'analytics_info_gen_ojo_anonimos'])
        + `</div>`
        + `</div>`;

      Swal.fire({
        title: this.$t('analytics_info_gen_titulo'),
        html,
        width: 'min(860px, 94vw)',
        confirmButtonText: this.$t('entendido'),
      });
    },

    mostrarInfo(clave) {
      const def = this.definicionesInfo()[clave];
      if (!def) return;

      let html = `<p class="text-left mb-0">${this.escaparHtml(def.texto)}</p>`;

      if (def.filas && def.filas.length) {
        const filas = def.filas
          .map(f => `<tr><td class="text-left"><code>${this.escaparHtml(f.clave)}</code></td>`
                  + `<td class="text-left">${this.escaparHtml(f.desc)}</td></tr>`)
          .join('');
        html += `<table class="table table-sm mt-3 mb-0"><thead><tr>`
              + `<th class="text-left">${this.escaparHtml(this.$t('analytics_cta_type'))}</th>`
              + `<th class="text-left">${this.escaparHtml(this.$t('analytics_info_cuando_se_registra'))}</th>`
              + `</tr></thead><tbody>${filas}</tbody></table>`;
      }

      if (def.nota) {
        html += `<p class="text-left text-muted small mt-3 mb-0">${this.escaparHtml(def.nota)}</p>`;
      }

      Swal.fire({
        title: def.titulo,
        html,
        icon: 'info',
        confirmButtonText: this.$t('cerrar'),
        width: def.filas ? 720 : 520,
      });
    },

    toIsoDate(d) {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    },
    formatDate(iso) {
      if (!iso) return '';
      const d = new Date(iso);
      return d.toLocaleDateString();
    },
    formatNumber(n) {
      if (n === null || n === undefined) return '0';
      return Number(n).toLocaleString();
    },
    hasData(chart) {
      return chart && chart.datasets && chart.datasets[0] && chart.datasets[0].data.length > 0;
    },
    buildPieChart(map) {
      if (!map) return null;
      const entries = Object.entries(map).filter(([, v]) => v > 0);
      if (entries.length === 0) return { labels: [], datasets: [{ data: [] }] };
      return {
        labels: entries.map(([k]) => k),
        datasets: [{
          data: entries.map(([, v]) => v),
          backgroundColor: entries.map((_, i) => PIE_COLORS[i % PIE_COLORS.length]),
        }],
      };
    },
    // Empresas y paises salen de dos fuentes: los ids con datos los da la API de analitica
    // (Mongo) y los nombres el portal (SQL). Se cargan una vez al entrar.
    async loadCatalogos() {
      try {
        const [empresasConDatos, empresasSql, paises, roles] = await Promise.all([
          analyticsService.getEmpresas(),
          empresaService.getEmpresasOptions().catch(() => []),
          paisService.getAll().catch(() => []),
          userService.getRolesOptions().catch(() => []),
        ]);

        const nombreEmpresa = {};
        (empresasSql || []).forEach(e => {
          const id = e.value !== undefined ? e.value : e.idEmpresa;
          const nombre = e.text !== undefined ? e.text : e.nombre;
          if (id !== undefined) nombreEmpresa[String(id)] = nombre;
        });

        // Solo las empresas que tienen productos publicados: ofrecer el catalogo completo
        // llenaria el selector de opciones que no devuelven nada.
        //
        // El conteo va con la palabra "productos" y no suelto entre parentesis: en una
        // pantalla de analitica un numero sin etiqueta se lee como visitas, y es el tamano
        // del catalogo. Finning tiene 43 productos y mas eventos que empresas con 500.
        //
        // El orden alfabetico se arma aca y no en la API: alla las empresas son solo ids
        // —los nombres salen de SQL, que esa API no alcanza—, asi que llegan ordenadas por
        // cantidad de productos y es este el unico lado que puede ordenarlas por nombre.
        this.empresasOptions = (empresasConDatos || []).map(e => {
          const nombre = nombreEmpresa[String(e.empresa_id)] || `#${e.empresa_id}`;
          return {
            empresa_id: e.empresa_id,
            // pais_id alimenta el selector de pais y el recorte de esta misma lista.
            pais_id: e.pais_id,
            nombre,
            label: `${nombre} — `
                 + this.$t('analytics_empresa_productos').replace('{n}', e.products),
          };
        }).sort((a, b) => a.nombre.localeCompare(b.nombre, 'es'));

        const mapaPaises = {};
        (paises || []).forEach(p => {
          const id = p.idPais !== undefined ? p.idPais : p.id;
          const nombre = p.nombre !== undefined ? p.nombre : p.Nombre;
          if (id !== undefined) mapaPaises[String(id)] = nombre;
        });
        this.paisesById = mapaPaises;

        // Catalogo de perfiles para el filtro de la columna Perfil en el detalle: se
        // ofrecen por nombre, que es lo que devuelve y compara perfilUsuario().
        this.perfilesOptions = (roles || [])
          .map(r => (r.text !== undefined ? r.text : r.Text))
          .filter(Boolean)
          .sort((a, b) => a.localeCompare(b, 'es'));
      } catch (err) {
        // Sin catalogos el dashboard sigue sirviendo: el selector queda vacio y los
        // paises se muestran por id, asi que no hay nada que avisar.
        this.empresasOptions = [];
      }
    },

    // Al cambiar el pais, una empresa ya elegida que no pertenece a el deja de estar en la
    // lista: se limpia, porque si no el filtro seguiria aplicandose sin que se vea cual es.
    onPaisChange() {
      if (this.empresaId && !this.empresasFiltradas.some(e => e.empresa_id === this.empresaId)) {
        this.empresaId = null;
      }
      this.loadSummary();
    },
    // Abre el detalle de una tarjeta: que registros hay detras del numero.
    async abrirDetalle(kpi) {
      this.detalleKpi = kpi;
      // Cada KPI tiene sus propias columnas (sesiones no tiene las mismas que impresiones),
      // asi que la busqueda y el orden se reinician al cambiar de tarjeta.
      // perfil, usuarioSeleccion y claveSeleccion son multi-select (checklist); el resto,
      // texto libre. clave/usuario mantienen su propio filtro de texto en paralelo
      // (ver detalleFilas).
      this.detalleFiltroCol = { perfil: [], usuarioSeleccion: [], claveSeleccion: [] };
      this.detalleOrdenColumna = '';
      this.detalleOrdenDireccion = 'asc';
      this.modalDetalle = true;
      await this.cargarDetalle(1);
    },

    // Ordenar/buscar por columna en el modal de detalle, igual que en /inventario.
    ordenarDetalle(columna) {
      if (this.detalleOrdenColumna === columna) {
        this.detalleOrdenDireccion = this.detalleOrdenDireccion === 'asc' ? 'desc' : 'asc';
      } else {
        this.detalleOrdenColumna = columna;
        this.detalleOrdenDireccion = 'asc';
      }
    },
    iconoOrdenDetalle(columna) {
      if (this.detalleOrdenColumna !== columna) return 'fas fa-sort text-muted';
      return this.detalleOrdenDireccion === 'asc' ? 'fas fa-sort-up' : 'fas fa-sort-down';
    },

    async cargarDetalle(page) {
      if (!this.detalleKpi) return;
      this.detalleLoading = true;
      this.detalleError = false;
      try {
        const [from, to] = this.dateRange;
        const fromIso = from ? new Date(`${from}T00:00:00Z`).toISOString() : null;
        const toIso = to ? new Date(`${to}T23:59:59Z`).toISOString() : null;

        // Mismo rango y mismos filtros que el resumen: si no, el total del modal no
        // coincidiria con el numero de la tarjeta que se acaba de presionar.
        this.detalle = await analyticsService.getDetail({
          kpi: this.detalleKpi.key,
          fromIso,
          toIso,
          empresaId: this.empresaId,
          paisId: this.paisId,
          page,
          limit: this.detalleLimit,
        });
        await this.resolverNombresUsuario(this.detalle.rows);
      } catch (err) {
        this.detalleError = true;
        this.detalle = { rows: [], total: 0, page: 1, mode: 'events' };
      } finally {
        this.detalleLoading = false;
      }
    },

    // Exporta TODO el detalle (no solo la pagina actual), respetando los mismos filtros con
    // que se abrio el modal. El backend tope el limit a 200 por pagina, asi que se pagina en
    // el cliente hasta juntar todas las filas.
    async exportarDetalleExcel() {
      if (!this.detalleKpi || this.exportandoDetalle) return;
      this.exportandoDetalle = true;
      try {
        const [from, to] = this.dateRange;
        const fromIso = from ? new Date(`${from}T00:00:00Z`).toISOString() : null;
        const toIso = to ? new Date(`${to}T23:59:59Z`).toISOString() : null;

        const limit = 200;
        let page = 1;
        let todasFilas = [];
        let total = Infinity;

        while (todasFilas.length < total) {
          const resp = await analyticsService.getDetail({
            kpi: this.detalleKpi.key,
            fromIso,
            toIso,
            empresaId: this.empresaId,
            paisId: this.paisId,
            page,
            limit,
          });
          total = resp.total || 0;
          const rows = resp.rows || [];
          todasFilas = todasFilas.concat(rows);
          if (!rows.length) break; // corte de seguridad si total viniera mal
          page++;
        }

        // Resuelve nombre/perfil/pais/empresa de TODOS los usuarios del export, no solo los
        // de la pagina que ya estaba cargada en pantalla.
        await this.resolverNombresUsuario(todasFilas);

        const datos = this.filasParaExportarDetalle(this.detalle.mode, todasFilas);
        if (!datos.length) {
          Swal.fire({ icon: 'info', title: this.$t('exportar_excel'), text: this.$t('analytics_no_data') });
          return;
        }

        const ws = XLSX.utils.json_to_sheet(datos);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Detalle');
        const fecha = new Date().toISOString().slice(0, 10);
        XLSX.writeFile(wb, `analytics_${this.detalleKpi.key}_${fecha}.xlsx`);
      } catch (err) {
        console.error('[exportarDetalleExcel] error:', err);
        Swal.fire({ icon: 'error', title: this.$t('analytics_error_generic') });
      } finally {
        this.exportandoDetalle = false;
      }
    },

    // Arma las filas planas para el Excel, con las mismas columnas que la tabla segun el modo.
    filasParaExportarDetalle(modo, rows) {
      if (modo === 'events') {
        return rows.map(r => ({
          [this.$t('analytics_det_fecha')]: this.formatDateTime(r.created_at),
          [this.$t('analytics_det_tipo')]: this.etiquetaTipo(r),
          [this.$t('analytics_det_dato')]: this.datoPrincipal(r) || '',
          [this.$t('analytics_det_pagina')]: r.page_path || '',
          [this.$t('analytics_det_dispositivo')]: r.device_type || '',
          [this.$t('analytics_det_idioma')]: r.locale || '',
          [this.$t('analytics_det_usuario')]: this.nombreUsuario(r.user_id),
          [this.$t('analytics_det_perfil')]: this.perfilUsuario(r.user_id) || '',
          [this.$t('analytics_det_pais')]: this.paisUsuario(r.user_id) || '',
          [this.$t('analytics_det_empresa')]: this.empresaUsuario(r.user_id) || '',
          [this.$t('analytics_det_sesion')]: r.session_id || '',
        }));
      }

      if (modo === 'sessions' || modo === 'users') {
        return rows.map(r => {
          const idUsuario = this.idUsuarioDetalle(r);
          const fila = {
            [modo === 'users' ? this.$t('analytics_det_usuario') : this.$t('analytics_det_sesion')]:
              modo === 'users' ? this.nombreUsuario(r.key) : r.key,
          };
          if (modo === 'sessions') {
            fila[this.$t('analytics_det_usuario')] = this.nombreUsuario(r.user_id);
          }
          fila[this.$t('analytics_det_perfil')] = this.perfilUsuario(idUsuario) || '';
          fila[this.$t('analytics_det_pais')] = this.paisUsuario(idUsuario) || '';
          fila[this.$t('analytics_det_empresa')] = this.empresaUsuario(idUsuario) || '';
          fila[this.$t('analytics_det_eventos')] = r.count;
          fila[this.$t('analytics_det_primero')] = this.formatDateTime(r.first);
          fila[this.$t('analytics_det_ultimo')] = this.formatDateTime(r.last);
          fila[this.$t('analytics_det_dispositivo')] = r.device_type || '';
          fila[this.$t('analytics_det_idioma')] = r.locale || '';
          return fila;
        });
      }

      if (modo === 'impressions') {
        return rows.map(r => ({
          [this.$t('analytics_product')]: r.label || r.key || '',
          [this.$t('analytics_impressions')]: r.count,
          [this.$t('analytics_det_ultimo')]: this.formatDateTime(r.last),
          [this.$t('analytics_det_pais')]: this.paisProducto(r) || '',
          [this.$t('analytics_det_empresa')]: this.empresaProducto(r) || '',
        }));
      }

      return [];
    },

    // Los eventos guardan el id del usuario, que viene de Mongo; el nombre, empresa y pais
    // viven en SQL. Se piden solo los ids de la pagina y en una sola llamada, y se cachean:
    // al paginar, los ya vistos no se vuelven a pedir.
    async resolverNombresUsuario(rows) {
      const modo = this.detalle.mode;
      const ids = new Set();
      (rows || []).forEach(r => {
        const id = modo === 'users' ? r.key : r.user_id;
        if (id && !(String(id) in this.usuariosById)) ids.add(String(id));
      });
      if (!ids.size) return;

      const nombres = await userService.getNombres([...ids]);
      const mapa = { ...this.usuariosById };
      (nombres || []).forEach(u => {
        const id = u.value !== undefined ? u.value : u.Value;
        const texto = u.text !== undefined ? u.text : u.Text;
        const rol = u.rol !== undefined ? u.rol : u.Rol;
        const empresa = u.empresa !== undefined ? u.empresa : u.Empresa;
        const pais = u.pais !== undefined ? u.pais : u.Pais;
        if (id !== undefined) mapa[String(id)] = { nombre: texto || '', perfil: rol || '', empresa: empresa || '', pais: pais || '' };
      });
      // Los que no volvieron se marcan igual, para no repedirlos en cada pagina.
      ids.forEach(id => { if (!(id in mapa)) mapa[id] = { nombre: '', perfil: '', empresa: '', pais: '' }; });
      this.usuariosById = mapa;
    },

    // En un CTA lo que importa es cual fue, no que haya sido un CTA: "cta_click" se repite
    // en todas las filas y no dice nada. El tipo original queda en el tooltip, y el color
    // del badge sigue identificando la familia del evento.
    //
    // Hace falta porque el cta_type no se ve en la columna Dato: 78 de 90 clics traen el
    // nombre del producto, que tiene prioridad ahi.
    etiquetaTipo(r) {
      return r.cta_type || r.event_type;
    },

    // Color del badge segun el tipo de evento. Se evita badge-secondary: en Argon el color
    // secundario es casi blanco, asi que el texto blanco encima quedaba ilegible.
    claseTipoEvento(tipo) {
      const colores = {
        product_view:   'badge-info',
        view_item_list: 'badge-primary',
        select_item:    'badge-warning',
        cta_click:      'badge-success',
        search:         'badge-danger',
        filter_apply:   'badge-danger',
        form_submit:    'badge-dark',
        modal_open:     'badge-default',
        engagement:     'badge-default',
      };
      return colores[tipo] || 'badge-dark';
    },

    // La ruta guardada en el evento apunta al ecommerce, no al portal. La base sale del
    // .env y se le quita la barra final para no terminar con dos al concatenar.
    urlPagina(path) {
      const base = (process.env.VUE_APP_ECOMMERCE_URL || 'https://ecommerce.inexlink.com').replace(/\/+$/, '');
      const ruta = String(path || '');
      return ruta.startsWith('/') ? base + ruta : `${base}/${ruta}`;
    },

    // Nombre del usuario, o el id si no se pudo resolver: perder la fila seria peor.
    nombreUsuario(id) {
      if (!id) return this.$t('analytics_det_anonimo');
      const u = this.usuariosById[String(id)];
      const nombre = u && u.nombre;
      return nombre ? nombre : String(id);
    },

    // En 'users' la fila ES el usuario (r.key); en 'sessions' el usuario es un dato aparte.
    idUsuarioDetalle(r) {
      return this.detalle.mode === 'users' ? r.key : r.user_id;
    },

    // Perfil/Pais/Empresa del usuario. Un evento anonimo (sin user_id) no tiene de donde sacarlos.
    perfilUsuario(id) {
      if (!id) return '';
      const u = this.usuariosById[String(id)];
      return (u && u.perfil) || '';
    },
    empresaUsuario(id) {
      if (!id) return '';
      const u = this.usuariosById[String(id)];
      return (u && u.empresa) || '';
    },
    paisUsuario(id) {
      if (!id) return '';
      const u = this.usuariosById[String(id)];
      return (u && u.pais) || '';
    },

    // Pais/Empresa del PRODUCTO (no del usuario): quien es dueño del material mostrado en la
    // impresion, resuelto contra los mismos catalogos que alimentan los filtros de arriba
    // (empresasOptions/paisesById), no contra usuariosById.
    empresaProducto(r) {
      const id = r && r.empresa_id;
      if (!id) return '';
      return this.empresasPorId[String(id)] || '';
    },
    paisProducto(r) {
      const id = r && r.pais_id;
      if (!id) return '';
      return this.paisesById[String(id)] || '';
    },

    cambiarPaginaDetalle(page) {
      if (page < 1 || page > this.detallePaginas) return;
      this.cargarDetalle(page);
    },

    // El dato que distingue a cada evento cambia segun el tipo: en una busqueda importa el
    // termino, en una vista el producto, en un CTA cual fue.
    datoPrincipal(r) {
      if (r.search_term) {
        const n = r.results_count;
        return n === undefined || n === null
          ? r.search_term
          : `${r.search_term} (${n})`;
      }
      if (r.product_name) return r.product_name;
      if (r.cta_type) return r.cta_type;
      if (r.product_id) return r.product_id;
      return '';
    },

    acorta(v) {
      const s = String(v || '');
      return s.length > 14 ? `${s.slice(0, 14)}…` : (s || '—');
    },

    formatDateTime(v) {
      if (!v) return '—';
      const d = new Date(v);
      if (Number.isNaN(d.getTime())) return '—';
      return d.toLocaleString();
    },

    async loadSummary() {
      this.loading = true;
      this.error = '';
      try {
        const [from, to] = this.dateRange;
        const fromIso = from ? new Date(`${from}T00:00:00Z`).toISOString() : null;
        const toIso = to ? new Date(`${to}T23:59:59Z`).toISOString() : null;
        this.summary = await analyticsService.getSummary(fromIso, toIso, this.empresaId, this.paisId);
      } catch (err) {
        if (err.response && err.response.status === 401) {
          this.error = this.$t('analytics_error_unauthorized');
          utilsService.logout();
        } else if (err.response && err.response.status === 403) {
          this.error = this.$t('analytics_error_forbidden');
        } else {
          this.error = this.$t('analytics_error_generic');
          console.error('analytics summary error:', err);
        }
      } finally {
        this.loading = false;
      }
    },
  },
  mounted() {
    this.$store.dispatch('setSidebarCollapsed', false);
    this.loadCatalogos();
    this.loadSummary();
  },
  beforeDestroy() {
    this.$store.dispatch('setSidebarCollapsed', false);
  },
};
</script>

<style scoped>
.card-stats .icon {
  width: 48px;
  height: 48px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
/* $green esta redefinido a #fff en el tema (_variables.scss), asi que bg-success
   pinta el circulo del mismo color que la tarjeta y queda invisible. Mismo parche
   que ya usa MaterialesView.vue; #2dce89 es el verde real de este tema (ver
   PIE_COLORS mas abajo). */
.card-stats .bg-success {
  background-color: #2dce89 !important;
}
/* Mismo bug de tema: btn-outline-success tambien sale de $success/$green (#fff), asi que
   quedaba blanco sobre blanco e invisible. Mismo verde real que el fix de arriba. */
.btn-outline-success {
  color: #2dce89 !important;
  border-color: #2dce89 !important;
}
.btn-outline-success:hover:not(:disabled) {
  background-color: #2dce89 !important;
  border-color: #2dce89 !important;
  color: #fff !important;
}
.bg-warning-soft {
  background-color: #fff8e1;
}
.table th {
  border-top: 0;
  font-size: 0.75rem;
  text-transform: uppercase;
  color: #8898aa;
}
.pie-wrapper {
  max-width: 280px;
  margin: 0 auto;
}

/* Las tarjetas de KPI abren el detalle, asi que tienen que verse pulsables */
.kpi-clickable {
  cursor: pointer;
  transition: transform 0.12s ease, box-shadow 0.12s ease;
}
.kpi-clickable:hover,
.kpi-clickable:focus {
  transform: translateY(-2px);
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.12);
  outline: none;
}

/* Sesiones, rutas y nombres de producto son largos: se cortan en vez de romper la tabla */
.td-elipsis {
  max-width: 260px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Cabecera fija del modal de detalle: con 892 registros hay que poder
   ubicar la columna sin perder de vista el titulo mientras se hace scroll */
.detalle-scroll thead th {
  position: sticky;
  top: 0;
  z-index: 2;
}

/* ===== Encabezados con busqueda y orden, igual que en /inventario ===== */
.th-ordenable {
  cursor: pointer;
  user-select: none;
  min-width: 90px;
  vertical-align: top;
  white-space: nowrap;
}
.th-ordenable:hover {
  background-color: #e9ecef !important;
}
.th-search-input {
  width: 100%;
  font-size: 11px;
  padding: 2px 4px;
  border: 1px solid #ced4da;
  border-radius: 3px;
  margin-bottom: 3px;
  display: block;
  font-weight: normal;
}
.th-label {
  display: block;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
}

/* Checklist de Perfil: mismo alto/tamano que los .th-search-input de al lado */
.th-filter-select {
  width: 100%;
  margin-bottom: 3px;
  display: block;
}
.th-filter-select >>> .el-input__inner {
  height: 22px;
  line-height: 22px;
  font-size: 11px;
  padding: 0 18px 0 6px;
  border-radius: 3px;
}
.th-filter-select >>> .el-input__icon {
  line-height: 22px;
}
</style>
